import { z } from "zod";
import { useEffect, useState } from "react";
import { Form, useFormHook } from "@/components/FormComps";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import useGetUserMe from "@/hooks/useGetUserMe";
import Snack from "./ui/Snack";
import { useRouter } from "next/router";
import Space from "./ui/Space";
import { sendGTMEvent } from "@next/third-parties/google";
import { GET_STUDENT_ACCOUNT } from "@/constants/queryKeys";

const schema = z.object({
  students: z.array(
    z.object({
      firstname: z.string().min(1, "Please enter student's first name"),
      lastname: z.string().min(1, "Please enter student's lastname"),
      email: z.string().optional().nullable(),
      yearOfBirth: z
        .string()
        .optional()
        .nullable()
        .refine((value) => !value || !isNaN(Number(value)), {
          message: "Please enter a valid year",
        })
        .transform((value) => (value ? Number(value) : null)),
    })
  ),
});

type Props = {
  redirectPath?: string;
};

export default function RegisterNewStudents({ redirectPath }: Props) {
  const router = useRouter();
  const { locale } = router;
  const userMeQ = useGetUserMe();
  const supabase = useSupabaseClient();
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState("");

  const [students, setStudents] = useState([{ firstname: "", lastname: "", email: "", yearOfBirth: null }]);

  const [checked, setChecked] = useState(false);

  const initialUserData = {
    firstname: userMeQ.data?.firstname || "",
    lastname: userMeQ.data?.lastname || "",
    email: userMeQ.data?.email || "",
    yearOfBirth: null,
  };

  const addStudent = () => {
    setStudents([...students, { firstname: "", lastname: "", email: "", yearOfBirth: null }]);
  };

  const removeStudent = (index) => {
    setStudents((prevStudents) => prevStudents.filter((_, i) => i !== index));
  };

  const onSubmit = async (formValues) => {
    setSuccessMessage("");
    try {
      for (const std of formValues.students) {
        if (std.email === userMeQ.data.email) {
          const { error: userAsStudentError } = await supabase.from("student").insert({
            id: userMeQ.data.payer.id,
            payerId: userMeQ.data.payer.id,
            first_name: userMeQ.data.firstname,
            last_name: userMeQ.data.lastname,
            dateJoined: new Date().toISOString(),
          });

          if (userAsStudentError) {
            throw userAsStudentError;
          }
        } else {
          const { yearOfBirth } = std;
          const { error: studentFormError } = await supabase.from("student").insert({
            payerId: userMeQ.data.payer.id,
            dateJoined: new Date().toISOString(),
            first_name: std.firstname,
            last_name: std.lastname,
            yearOfBirth,
          });

          if (studentFormError) {
            throw studentFormError;
          }
        }
      }
      queryClient.invalidateQueries({ queryKey: ["studentChecker"] });
      queryClient.invalidateQueries({ queryKey: [GET_STUDENT_ACCOUNT], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["introductoryMeetingRequestStudents-1"] });
      queryClient.invalidateQueries({ queryKey: ["workshopRequestStudents-1"] });
      setSuccessMessage(locale === "fa" ? "ثبت‌نام با موفقیت انجام شد" : "Registration successful");
      redirectPath && router.push(redirectPath);
    } catch (error) {
      toast.error(
        locale === "fa" ? "خطایی رخ داده است. لطفا دوباره تلاش کنید." : "An error occurred. Please try again."
      );
      console.error(error);
    }
  };

  useEffect(() => {
    if (checked) {
      if (students.length === 1) {
        setStudents([initialUserData]);
      } else {
        // Insert the user data at the first position
        setStudents((prevStudents) => [initialUserData, ...prevStudents]);
      }
    } else {
      if (students.length === 1) {
        setStudents([{ firstname: "", lastname: "", email: "", yearOfBirth: null }]);
      } else {
        // Remove the first entry if it's the user
        setStudents((prevStudents) =>
          prevStudents.filter((_, i) => i !== 0 || prevStudents[0].email !== initialUserData.email)
        );
      }
    }
  }, [checked]);

  const form = useFormHook({ schema: schema, onSubmit });

  const order = {
    1: "First",
    2: "Second",
    3: "Third",
    4: "Fourth",
    5: "Fifth",
    6: "Sixth",
    7: "Seventh",
    8: "Eighth",
    9: "Ninth",
    10: "Tenth",
  };

  const orderFa = {
    1: "اولین",
    2: "دومین",
    3: "سومین",
    4: "چهارمین",
    5: "پنجمین",
    6: "ششمین",
    7: "هفتمین",
    8: "هشتمین",
    9: "نهمین",
    10: "دهمین",
  };

  const currentYear = new Date().getFullYear();
  const yearOfBirthOptions = [];

  for (let year = currentYear; year >= currentYear - 100; year--) {
    yearOfBirthOptions.push([year.toString(), year.toString()]);
  }

  return (
    <div className="space-y-2 b-1 b-solid rd-lg p-4 bg-gray1">
      <div className="space-y-4">
        <Form form={form} onSubmit={onSubmit} className="space-y-2" hasSubmitButton={false}>
          <Snack variant="info">
            <p>{locale === "fa" ? "اطلاعات شاگردان را وارد کنید" : "Add student information"}</p>
          </Snack>
          {userMeQ.data && !userMeQ.data.isStudent && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="studentId"
                checked={checked}
                onChange={() => {
                  setChecked(!checked);
                  sendGTMEvent({ event: "themselves-student-checkbox" });
                }}
                className="form-checkbox"
              />
              <label htmlFor="studentId">
                {locale === "fa" ? "اگر خودتان شاگرد هستید، اینجا را کلیک کنید." : "Click here if you are the student."}
              </label>
            </div>
          )}
          {students.map((student, index) => (
            <div key={index}>
              <p className="font-bold">
                {locale === "fa"
                  ? `اطلاعات ${orderFa[index + 1]} شاگرد را وارد کنید`
                  : `Enter the ${order[index + 1]} Sudent Information`}
              </p>
              <Form.Input
                name={`students[${index}].firstname`}
                label={locale === "fa" ? "نام" : "First Name"}
                defaultValue={student.firstname}
                readOnly={student.email === initialUserData.email}
                required
              />
              <Form.ErrorMessage name={`students[${index}].firstname`} />
              <Form.Input
                name={`students[${index}].lastname`}
                label={locale === "fa" ? "نام خانوادگی" : "Last Name"}
                defaultValue={student.lastname}
                readOnly={student.email === initialUserData.email}
                required
              />
              <Form.ErrorMessage name={`students[${index}].lastname`} />
              <Form.Select
                name={`students[${index}].yearOfBirth`}
                label={locale === "fa" ? "سال تولد" : "Year of Birth"}
                options={yearOfBirthOptions}
                defaultValue={student.yearOfBirth}
                readOnly={student.email === initialUserData.email}
              />
              <Form.ErrorMessage name={`students[${index}].yearOfBirth`} />
              <div className="hidden">
                <Form.Input
                  name={`students[${index}].email`}
                  label="Email"
                  defaultValue={student.email}
                  readOnly={student.email === initialUserData.email}
                />
              </div>
              {index !== 0 || !checked ? (
                <button type="button" onClick={() => removeStudent(index)}>
                  {locale === "fa" ? "حذف شاگرد" : "Remove Student"}
                </button>
              ) : null}
              <Space size="h-5" />
            </div>
          ))}
          {successMessage && <p className="c-green11 font-bold text-lg mb-4">{successMessage}</p>}
          <button
            type="button"
            onClick={() => {
              addStudent;
              sendGTMEvent({ event: "add-another-student-click" });
            }}
            className="fw-500 c-prm11 hover:c-prm9 block"
          >
            {locale === "fa" ? "اضافه کردن شاگرد دیگر" : "Add Another Student"}
          </button>
          <Form.SubmitButton width="parent" onClick={() => sendGTMEvent({ event: "submit-new-student-click" })}>
            {locale === "en" ? "Submit" : "ثبت"}
          </Form.SubmitButton>
        </Form>
      </div>
    </div>
  );
}
